import saveAs from "file-saver";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Template from "@/assets/templates/upload-bulk-clients-template.xlsx";
import { Button, Icon, Typography } from "@/components/atoms";
import { InviteClientsModal } from "@/components/organisms/Clients/modals/InviteClientsModal";
import { useOperator } from "@/hooks";
import { useGetDeepLinksQuery } from "@/redux/apis/config/linksAndDocuments";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { useAppSelector } from "@/redux/hooks";
import { registrationSelector } from "@/redux/slices/marketing/selectors";
import { OnboardingClientsSection } from "@/redux/slices/onboarding/types";
import { copyToClipboard, openUrlWithGoogleDriveSupport } from "@/utils";
import { SetupPanel, SetupPanelItem, WhyDoINeedModal, WalkthroughModal, ReadSetupGuideModal } from "../common";
import { quickSetupContent, quickSetupExplanations } from "../data/quickSetupContent";
import { isSectionComplete, isPostSimulationSectionsUnlocked } from "../helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../hooks";

const ClientCompanyPaths = {
  ADD_CLIENT: "/clients/add",
  CLIENTS: "/clients",
  ADD_COMPANY: "/companies/add",
} as const;

enum ModalType {
  REGISTER_CLIENT_WHY = "register-client-why-do-i-need",
  REGISTER_CLIENT_WALKTHROUGH = "register-client-walkthrough",
  REGISTER_CLIENT_DOCUMENTATION = "register-client-documentation",
  UPLOAD_CLIENTS_WHY = "upload-clients-why-do-i-need",
  UPLOAD_CLIENTS_WALKTHROUGH = "upload-clients-walkthrough",
  UPLOAD_CLIENTS_DOCUMENTATION = "upload-clients-documentation",
  COMPANY_PROFILES_WHY = "company-profiles-why-do-i-need",
  COMPANY_PROFILES_WALKTHROUGH = "company-profiles-walkthrough",
  COMPANY_PROFILES_DOCUMENTATION = "company-profiles-documentation",
}

export const ClientCompanyOnboardingPanel = () => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [showInviteClientsModal, setShowInviteClientsModal] = useState(false);

  const { providerCode } = useOperator();
  const { isFetching } = useGetDeepLinksQuery();
  const { deepLinks } = useAppSelector(registrationSelector);
  const registration = deepLinks.find((link) => link.document.slug === "client-registration");

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isRegisterClientCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.onboardingClients.registerClient) : false;
  const isUploadClientsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.onboardingClients.uploadClients) : false;
  const isCompanyProfilesCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.onboardingClients.setupCompanyProfile) : false;

  const completedSteps = [isRegisterClientCompleted, isUploadClientsCompleted, isCompanyProfilesCompleted].filter(Boolean).length;
  const totalSteps = 3;

  const isUnlocked = isPostSimulationSectionsUnlocked(onboardingStatus);

  const handleModalClose = () => setActiveModal(null);

  const handleCopyRegistrationLink = async () => {
    if (registration?.document.link) {
      copyToClipboard(registration.document.link);

      if (onboardingStatus && !isRegisterClientCompleted) {
        try {
          await updateSectionStatus({ section: OnboardingClientsSection.REGISTER_CLIENT, completed: true });
        } catch (error) {
          console.error("Failed to update section status:", error);
        }
      }
    }
  };

  const handleDownloadQRCode = async () => {
    setShowInviteClientsModal(true);

    if (onboardingStatus && !isRegisterClientCompleted) {
      try {
        await updateSectionStatus({ section: OnboardingClientsSection.REGISTER_CLIENT, completed: true });
      } catch (error) {
        console.error("Failed to update section status:", error);
      }
    }
  };

  const handleDownloadEmailTemplate = () => {
    openUrlWithGoogleDriveSupport("https://drive.google.com/file/u/1/d/1zPSC2OWJNYCdC7XbL7bQabV6fE_DUXr3/view?usp=drive_link");
  };

  const handleDownloadBulkUploadTemplate = async () => {
    if (onboardingStatus && !isUploadClientsCompleted) {
      try {
        await updateSectionStatus({ section: OnboardingClientsSection.UPLOAD_CLIENTS, completed: true });
      } catch (error) {
        console.error("Failed to update section status:", error);
      }
    }

    saveAs(Template, "upload-bulk-clients-template");
  };

  // Register Client handlers
  const handleRegisterClientAction = () => {
    setQuickSetupFlag(OnboardingClientsSection.REGISTER_CLIENT);
    navigate(ClientCompanyPaths.ADD_CLIENT);

    if (onboardingStatus && !isRegisterClientCompleted) {
      updateSectionStatus({ section: OnboardingClientsSection.REGISTER_CLIENT, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleRegisterClientWhyDoThis = () => {
    setActiveModal(ModalType.REGISTER_CLIENT_WHY);
  };

  const handleRegisterClientWatchVideo = () => {
    setActiveModal(ModalType.REGISTER_CLIENT_WALKTHROUGH);
  };

  const handleRegisterClientReadDocumentation = () => {
    setActiveModal(ModalType.REGISTER_CLIENT_DOCUMENTATION);
  };

  // Upload Clients handlers
  const handleUploadClientsAction = () => {
    setQuickSetupFlag(OnboardingClientsSection.UPLOAD_CLIENTS);
    navigate(ClientCompanyPaths.CLIENTS);

    if (onboardingStatus && !isUploadClientsCompleted) {
      updateSectionStatus({ section: OnboardingClientsSection.UPLOAD_CLIENTS, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleUploadClientsWhyDoThis = () => {
    setActiveModal(ModalType.UPLOAD_CLIENTS_WHY);
  };

  const handleUploadClientsWatchVideo = () => {
    setActiveModal(ModalType.UPLOAD_CLIENTS_WALKTHROUGH);
  };

  const handleUploadClientsReadDocumentation = () => {
    setActiveModal(ModalType.UPLOAD_CLIENTS_DOCUMENTATION);
  };

  // Company Profiles handlers
  const handleCompanyProfilesAction = () => {
    setQuickSetupFlag(OnboardingClientsSection.SETUP_COMPANY_PROFILE);
    navigate(ClientCompanyPaths.ADD_COMPANY);

    if (onboardingStatus && !isCompanyProfilesCompleted) {
      updateSectionStatus({ section: OnboardingClientsSection.SETUP_COMPANY_PROFILE, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleCompanyProfilesWhyDoThis = () => {
    setActiveModal(ModalType.COMPANY_PROFILES_WHY);
  };

  const handleCompanyProfilesWatchVideo = () => {
    setActiveModal(ModalType.COMPANY_PROFILES_WALKTHROUGH);
  };

  const handleCompanyProfilesReadDocumentation = () => {
    setActiveModal(ModalType.COMPANY_PROFILES_DOCUMENTATION);
  };

  const registerClientFeaturedIcon = {
    name: "User",
    background: "bg-primary-light",
  };

  const uploadClientsFeaturedIcon = {
    name: "People",
    background: "bg-primary-light",
  };

  const companyProfilesFeaturedIcon = {
    name: "Buildings",
    background: "bg-primary-light",
  };

  const transportProviderCodeContent = (
    <div className="rounded-lg bg-neutral-surface-gray p-2.5">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2.5 md:flex-row md:items-center">
          <div className="flex items-center gap-1">
            <Icon isCustom name="app-google-playstore" size="sm" />
            <Icon isCustom name="app-ios" size="sm" />
          </div>
          <Typography variant="action" className="text-neutral-dark-gray">
            Transport Provider Code:
          </Typography>
          <div className="flex items-center gap-1">
            <Typography variant="paragraph" className="uppercase text-neutral-dark-gray">
              {providerCode}
            </Typography>
            <Icon name="InfoCircle" size="md" className="text-neutral-silver-gray" />
          </div>
        </div>
        <div className="flex flex-col gap-2.5 md:flex-row md:gap-6">
          <Button
            variant="tertiary-light"
            size="xs"
            className="h-5 items-center gap-2 self-start !p-0 hover:bg-transparent"
            startIcon={<Icon name="Copy" size="md" className="text-info" />}
            onClick={handleCopyRegistrationLink}
            disabled={isFetching || !registration?.document.link}
          >
            <Typography variant="action" className="text-info">
              Copy Registration Link
            </Typography>
          </Button>
          <Button
            variant="tertiary-light"
            size="xs"
            className="h-5 items-center gap-2 self-start !p-0 hover:bg-transparent"
            startIcon={<Icon name="ImportCurve" size="md" className="text-info" />}
            onClick={handleDownloadQRCode}
          >
            <Typography variant="action" className="text-info">
              Download QR Code
            </Typography>
          </Button>
        </div>
      </div>
    </div>
  );

  const bulkUploadToolsContent = (
    <div className="rounded-lg bg-neutral-surface-gray p-2.5">
      <div className="flex flex-col gap-3">
        <Typography variant="action" className="text-neutral-dark-gray">
          Bulk Upload Tools:
        </Typography>
        <div className="flex flex-col gap-2.5 md:flex-row md:gap-6">
          <Button
            variant="tertiary-light"
            size="xs"
            className="h-5 items-center gap-2 self-start !p-0 hover:bg-transparent"
            startIcon={<Icon name="ImportCurve" size="md" className="text-info" />}
            onClick={handleDownloadEmailTemplate}
          >
            <Typography variant="action" className="text-info">
              Download Email Template
            </Typography>
          </Button>
          <Button
            variant="tertiary-light"
            size="xs"
            className="h-5 items-center gap-2 self-start !p-0 hover:bg-transparent"
            startIcon={<Icon name="ImportCurve" size="md" className="text-info" />}
            endIcon={<Icon isCustom name="excel" size="sm" />}
            onClick={handleDownloadBulkUploadTemplate}
          >
            <Typography variant="action" className="text-info">
              <span className="hidden md:inline">Download Bulk Upload Template</span>
              <span className="md:hidden">Download Bulk Upload</span>
            </Typography>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <SetupPanel
      stepNumber={5}
      title="Onboarding Clients and Companies"
      description="Register clients and companies so they can start booking and managing trips with you"
      stepCount={totalSteps}
      completedSteps={completedSteps}
      isLocked={!isUnlocked}
    >
      <SetupPanelItem
        icon={registerClientFeaturedIcon.name}
        iconBackground={registerClientFeaturedIcon.background}
        title="Register a Client"
        isCompleted={isRegisterClientCompleted}
        actions={[
          {
            label: "Update Details",
            onClick: handleRegisterClientAction,
            variant: "primary",
          },
        ]}
        additionalContent={transportProviderCodeContent}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleRegisterClientWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleRegisterClientWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleRegisterClientReadDocumentation },
        ]}
      />

      <SetupPanelItem
        icon={uploadClientsFeaturedIcon.name}
        iconBackground={uploadClientsFeaturedIcon.background}
        title="Upload Clients in Bulk"
        isCompleted={isUploadClientsCompleted}
        actions={[
          {
            label: "Update Details",
            onClick: handleUploadClientsAction,
            variant: "primary",
          },
        ]}
        additionalContent={bulkUploadToolsContent}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleUploadClientsWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleUploadClientsWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleUploadClientsReadDocumentation },
        ]}
      />

      <SetupPanelItem
        icon={companyProfilesFeaturedIcon.name}
        iconBackground={companyProfilesFeaturedIcon.background}
        title="Setup Company Profiles"
        isCompleted={isCompanyProfilesCompleted}
        actions={[
          {
            label: "Update Details",
            onClick: handleCompanyProfilesAction,
            variant: "primary",
          },
        ]}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleCompanyProfilesWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleCompanyProfilesWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleCompanyProfilesReadDocumentation },
        ]}
      />

      {/* Register Client Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.REGISTER_CLIENT_WHY}
        onClose={handleModalClose}
        title="Why do I need to register clients?"
        featuredIcon={registerClientFeaturedIcon}
        explanations={quickSetupExplanations.clientCompanyOnboarding.registerClient}
      />

      <WalkthroughModal
        featuredIcon={registerClientFeaturedIcon}
        open={activeModal === ModalType.REGISTER_CLIENT_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Client Registration)"
        videoUrl={quickSetupContent.clientCompanyOnboarding.registerClient.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={registerClientFeaturedIcon}
        open={activeModal === ModalType.REGISTER_CLIENT_DOCUMENTATION}
        onClose={handleModalClose}
        title="Client Registration Setup Guide"
        documentationUrl={quickSetupContent.clientCompanyOnboarding.registerClient.documentationUrl}
        pdfUrl={quickSetupContent.clientCompanyOnboarding.registerClient.pdfUrl}
      />

      {/* Upload Clients Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.UPLOAD_CLIENTS_WHY}
        onClose={handleModalClose}
        title="Why do I need bulk client upload?"
        featuredIcon={uploadClientsFeaturedIcon}
        explanations={quickSetupExplanations.clientCompanyOnboarding.uploadClients}
      />

      <WalkthroughModal
        featuredIcon={uploadClientsFeaturedIcon}
        open={activeModal === ModalType.UPLOAD_CLIENTS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Bulk Upload)"
        videoUrl={quickSetupContent.clientCompanyOnboarding.uploadClients.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={uploadClientsFeaturedIcon}
        open={activeModal === ModalType.UPLOAD_CLIENTS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Bulk Client Upload Setup Guide"
        documentationUrl={quickSetupContent.clientCompanyOnboarding.uploadClients.documentationUrl}
        pdfUrl={quickSetupContent.clientCompanyOnboarding.uploadClients.pdfUrl}
      />

      {/* Company Profiles Modals */}
      <WhyDoINeedModal
        open={activeModal === ModalType.COMPANY_PROFILES_WHY}
        onClose={handleModalClose}
        title="Why do I need to setup company profiles?"
        featuredIcon={companyProfilesFeaturedIcon}
        explanations={quickSetupExplanations.clientCompanyOnboarding.companyProfiles}
      />

      <WalkthroughModal
        featuredIcon={companyProfilesFeaturedIcon}
        open={activeModal === ModalType.COMPANY_PROFILES_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Company Profile Setup)"
        videoUrl={quickSetupContent.clientCompanyOnboarding.companyProfiles.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={companyProfilesFeaturedIcon}
        open={activeModal === ModalType.COMPANY_PROFILES_DOCUMENTATION}
        onClose={handleModalClose}
        title="Company Profile Setup Guide"
        documentationUrl={quickSetupContent.clientCompanyOnboarding.companyProfiles.documentationUrl}
        pdfUrl={quickSetupContent.clientCompanyOnboarding.companyProfiles.pdfUrl}
      />

      {/* Invite Clients Modal */}
      <InviteClientsModal open={showInviteClientsModal} onClose={() => setShowInviteClientsModal(false)} />
    </SetupPanel>
  );
};
